/**
 * Translation Hooks
 * ============================================================================
 *
 * Purpose:
 * --------
 * Provides type-safe translation hooks and formatting utilities for
 * React components.
 *
 * This module exports:
 * - useTranslation: Type-safe wrapper around i18next translation hook
 * - useCurrencyFormatter: East African currency formatting
 * - useDateFormatter: Locale-aware date formatting
 * - useNumberFormatter: Locale-aware number formatting
 *
 * Design principles:
 * ------------------
 * - All hooks are type-safe with namespace support
 * - Currency formatting supports TZS, KES, UGX, USD with locale awareness
 * - Date formatting defaults to DD/MM/YYYY but respects user preferences
 * - Formatting utilities use Intl API for proper localization
 *
 * Non-goals:
 * ----------
 * - Managing language switching (use LanguageSwitcher component)
 * - Storing user preferences (handled by settings module)
 * - Translation file management
 *
 * Changes to this file affect how translations and formatting are
 * consumed throughout the application.
 * ============================================================================
 */

import { useTranslation as useI18nTranslation } from 'react-i18next';
import type { TranslationNamespace } from 'locales';
import type { DateFormat, SupportedCurrency } from './types';

/**
 * Type-safe translation hook
 *
 * Provides translation functions with namespace support and
 * type-safe key autocomplete when using TypeScript.
 *
 * @param namespace - Optional translation namespace (defaults to 'common')
 * @returns Translation function and language utilities
 *
 * @example
 * const { t } = useTranslation('policies');
 * t('title'); // "Insurance Policies" or "Bima"
 */
export const useTranslation = (namespace?: TranslationNamespace) => {
  const { t, i18n } = useI18nTranslation(namespace);

  return {
    /**
     * Translation function with interpolation support
     */
    t,

    /**
     * Current language code (en, sw)
     */
    language: i18n.language,

    /**
     * Change application language
     *
     * Updates i18n instance and persists choice to localStorage.
     */
    changeLanguage: (lng: string) => i18n.changeLanguage(lng),

    /**
     * Check if current language is RTL
     *
     * Returns false for current supported languages (en, sw).
     * Prepared for future RTL language support.
     */
    isRTL: i18n.dir() === 'rtl',

    /**
     * Check if translations are ready
     */
    isReady: i18n.isInitialized,
  };
};

/**
 * Currency formatter for East African currencies
 *
 * Formats monetary amounts according to locale and currency conventions.
 * Defaults to TZS (Tanzanian Shilling) but supports multi-currency.
 *
 * @returns Currency formatting function
 *
 * @example
 * const { formatCurrency } = useCurrencyFormatter();
 * formatCurrency(50000, 'TZS'); // "TSh 50,000" (in Swahili locale)
 * formatCurrency(50000, 'USD'); // "$50,000.00" (in English locale)
 */
export const useCurrencyFormatter = () => {
  const { language } = useTranslation();

  /**
   * Format amount as currency
   *
   * Uses Intl.NumberFormat for locale-aware formatting.
   * East African currencies typically don't use decimal places.
   */
  const formatCurrency = (amount: number, currency: SupportedCurrency = 'TZS'): string => {
    // East African currencies (TZS, KES, UGX) don't use fractional units
    const useDecimals = currency === 'USD';

    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency,
      minimumFractionDigits: useDecimals ? 2 : 0,
      maximumFractionDigits: useDecimals ? 2 : 0,
    }).format(amount);
  };

  /**
   * Format amount as compact currency (e.g., "50K", "1.2M")
   *
   * Useful for displaying large amounts in limited space.
   */
  const formatCompactCurrency = (amount: number, currency: SupportedCurrency = 'TZS'): string => {
    const formatter = new Intl.NumberFormat(language, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    });

    return formatter.format(amount);
  };

  return {
    formatCurrency,
    formatCompactCurrency,
  };
};

/**
 * Date formatter with user preference support
 *
 * Formats dates according to locale and user-configured format preference.
 * Defaults to DD/MM/YYYY (East African standard).
 *
 * @param formatPreference - Optional date format override
 * @returns Date formatting utilities
 *
 * @example
 * const { formatDate } = useDateFormatter();
 * formatDate(new Date()); // "11/01/2026" (DD/MM/YYYY)
 */
export const useDateFormatter = (formatPreference?: DateFormat) => {
  const { language } = useTranslation();

  // Get user's date format preference from localStorage or use default
  const userFormat =
    formatPreference ?? (localStorage.getItem('bimanova_date_format') as DateFormat) ?? 'DD/MM/YYYY';

  /**
   * Format date according to user preference
   */
  const formatDate = (date: Date | string | number): string => {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

    // Map format preference to Intl.DateTimeFormat options
    const formatOptions: Record<DateFormat, Intl.DateTimeFormatOptions> = {
      'DD/MM/YYYY': { day: '2-digit', month: '2-digit', year: 'numeric' },
      'MM/DD/YYYY': { month: '2-digit', day: '2-digit', year: 'numeric' },
      'YYYY-MM-DD': { year: 'numeric', month: '2-digit', day: '2-digit' },
    };

    const options = formatOptions[userFormat];
    const formatted = new Intl.DateTimeFormat(language, options).format(dateObj);

    // Intl returns format based on locale, so we might need to rearrange
    if (userFormat === 'YYYY-MM-DD') {
      const parts = dateObj.toISOString().split('T')[0];
      return parts ?? formatted;
    }

    return formatted;
  };

  /**
   * Format date and time
   */
  const formatDateTime = (date: Date | string | number): string => {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

    return new Intl.DateTimeFormat(language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  };

  /**
   * Format as relative time (e.g., "2 days ago", "in 3 hours")
   */
  const formatRelativeTime = (date: Date | string | number): string => {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    const now = new Date();
    const diffMs = dateObj.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (Math.abs(diffDays) < 1) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        return language === 'sw' ? 'Sasa hivi' : 'Just now';
      }
      return new Intl.RelativeTimeFormat(language, { numeric: 'auto' }).format(diffHours, 'hour');
    }

    return new Intl.RelativeTimeFormat(language, { numeric: 'auto' }).format(diffDays, 'day');
  };

  return {
    formatDate,
    formatDateTime,
    formatRelativeTime,
    currentFormat: userFormat,
  };
};

/**
 * Number formatter for locale-aware number display
 *
 * @returns Number formatting utilities
 *
 * @example
 * const { formatNumber } = useNumberFormatter();
 * formatNumber(1234567.89); // "1,234,567.89" (en) or "1 234 567,89" (sw)
 */
export const useNumberFormatter = () => {
  const { language } = useTranslation();

  /**
   * Format number with locale-aware separators
   */
  const formatNumber = (num: number, decimals = 2): string => {
    return new Intl.NumberFormat(language, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  /**
   * Format as percentage
   */
  const formatPercentage = (num: number, decimals = 0): string => {
    return new Intl.NumberFormat(language, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  /**
   * Format as compact notation (e.g., "1.2K", "3.4M")
   */
  const formatCompact = (num: number): string => {
    return new Intl.NumberFormat(language, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(num);
  };

  return {
    formatNumber,
    formatPercentage,
    formatCompact,
  };
};
